import safeURLParse from './safeURLParse'

const FORCE_WEB_VIEW_QUERY_PARAM = 'fwv'
const FORCE_HANDOFF_QUERY_PARAM = 'force_safari'

const INSTAGRAM_USER_AGENT_PATTERN = /\bInstagram\b/
const FACEBOOK_USER_AGENT_PATTERN = /FBAN\/FBIOS/
const TWITTER_UA = /\bTwitter\b/
const GOOGLE_WEB_VIEW_USER_AGENT_PATTERN = /\bGoogleWv\b/
// only a branded token proves a real browser: web views fake the Safari/ one, and Safari itself ships no bridge
const IOS_BROWSER_USER_AGENT_PATTERN = /\b(?:CriOS|FxiOS|EdgiOS|OPiOS|OPT|YaBrowser|DuckDuckGo)\b/

export type InAppBrowser = 'ios-webview' | 'google-webview' | 'instagram' | 'facebook'

export type BrowserHandoff = {
    browser: InAppBrowser
    method: 'location' | 'meta-refresh'
    url: string
}

interface InAppBrowserEnvironment {
    self: unknown
    top: unknown
    location: Pick<Location, 'href'>
    navigator: Pick<Navigator, 'maxTouchPoints' | 'userAgent'>
    webkit?: {
        messageHandlers?: {
            playgamaApp?: unknown
        }
    }
}

interface HandoffEnvironment {
    location: Pick<Location, 'assign' | 'replace'>
    document: Pick<Document, 'createElement' | 'head'>
}

export function detectInAppBrowser(environment: InAppBrowserEnvironment = window): InAppBrowser | undefined {
    const { maxTouchPoints, userAgent } = environment.navigator
    const isIos = /(iPhone|iPad|iPod)/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)

    if (!isIos) {
        return undefined
    }

    const messageHandlers = environment.webkit?.messageHandlers
    // our own iOS app is a WKWebView too, and it must never be handed off
    if (messageHandlers?.playgamaApp) {
        return undefined
    }
    if (TWITTER_UA.test(userAgent)) {
        return undefined
    }
    if (GOOGLE_WEB_VIEW_USER_AGENT_PATTERN.test(userAgent)) {
        return 'google-webview'
    }
    if (INSTAGRAM_USER_AGENT_PATTERN.test(userAgent)) {
        return 'instagram'
    }
    if (FACEBOOK_USER_AGENT_PATTERN.test(userAgent)) {
        return 'facebook'
    }

    if (messageHandlers && !IOS_BROWSER_USER_AGENT_PATTERN.test(userAgent)) {
        return 'ios-webview'
    }

    return undefined
}

export function resolveBrowserHandoff(environment: InAppBrowserEnvironment = window): BrowserHandoff | undefined {
    if (environment.self !== environment.top) {
        return undefined
    }

    const url = safeURLParse(environment.location.href)
    if (!url || (url.protocol !== 'http:' && url.protocol !== 'https:')) {
        return undefined
    }
    if (url.searchParams.get(FORCE_WEB_VIEW_QUERY_PARAM) === '1') {
        return undefined
    }

    const forced = url.searchParams.get(FORCE_HANDOFF_QUERY_PARAM) === '1'
    const browser = detectInAppBrowser(environment) ?? (forced ? 'ios-webview' : undefined)
    if (!browser) {
        return undefined
    }

    url.searchParams.delete(FORCE_HANDOFF_QUERY_PARAM)
    url.searchParams.set(FORCE_WEB_VIEW_QUERY_PARAM, '1')

    if (browser === 'instagram') {
        return {
            browser,
            method: 'meta-refresh',
            url: `instagram://extbrowser/?url=${encodeURIComponent(url.href)}`,
        }
    }

    return {
        browser,
        method: 'location',
        url: url.href.replace(/^https?:/, 'x-safari-https:'),
    }
}

export function requestBrowserHandoff(handoff: BrowserHandoff, environment: HandoffEnvironment = window): void {
    if (handoff.method === 'location') {
        environment.location.replace(handoff.url)
        return
    }

    const meta = environment.document.createElement('meta')
    meta.httpEquiv = 'refresh'
    meta.content = `0;url=${handoff.url}`
    environment.document.head.appendChild(meta)
}

export function openBrowserHandoff(
    handoff: BrowserHandoff,
    environment: Pick<HandoffEnvironment, 'location'> = window,
): void {
    environment.location.assign(handoff.url)
}
